import React, { useState } from 'react';
import { Button, Descriptions, Modal, Spin, Table, Tag } from 'antd';
import moment from 'moment';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { fmcsa } from '../../../../../API/TT_ELD/fmcsa';
import { calcOrder } from '../../../../components/core/calcOrder';

const { confirm } = Modal;

type FailedItem = {
  no: number;
  key: React.Key;
  id: string | number;
  driver: string;
  company: string;
  startDate: string;
  endDate: string;
  createdAt: string;
  status: string;
  error: string;
  raw: any;
};

const getByPath = (source: any, path: string) =>
  path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), source);

const getFirstValue = (source: any, paths: string[]) => {
  for (const path of paths) {
    const value = getByPath(source, path);
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }
  return undefined;
};

const formatDateTime = (value: any) => {
  if (!value) return '-';
  const date = moment(value);
  if (!date.isValid()) return String(value);
  return date.format('MM-DD-YYYY HH:mm:ss');
};

const stringifyDetails = (value: any) => {
  if (value === undefined || value === null) return '-';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value);
  }
};

const FmcsaFailedFromApp = ({
  data = [],
  onChange,
  total = 0,
  isLoading,
  isFetching,
  refetch,
  skip,
}: {
  data: Array<any>;
  onChange(current: any): void;
  total: number | undefined;
  isLoading: boolean;
  isFetching: boolean;
  refetch(): void;
  skip: number;
}) => {
  const [retryingId, setRetryingId] = useState<string | number | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState<any>(null);

  const handleRetry = (id: string | number) => {
    confirm({
      title: 'Retry FMCSA report',
      icon: <ExclamationCircleFilled />,
      content: 'Are you sure you want to retry sending this FMCSA report?',
      onOk: async () => {
        try {
          setRetryingId(id);
          await fmcsa.fmcsaFailedRetry(id);
          refetch();
        } finally {
          setRetryingId(null);
        }
      },
    });
  };

  const handleShowDetails = (row: FailedItem) => {
    setSelectedDetails(row.raw || null);
    setDetailsOpen(true);
  };

  const renderRetriedBy = (raw: any) => {
    const firstName = getFirstValue(raw, [
      'retriedBy.first_name',
      'retriedBy.firstName',
      'retriedById.first_name',
      'retriedById.firstName',
      'retriedByUser.first_name',
      'retriedByUser.firstName',
    ]);
    const secondName = getFirstValue(raw, [
      'retriedBy.second_name',
      'retriedBy.last_name',
      'retriedBy.lastName',
      'retriedById.second_name',
      'retriedById.last_name',
      'retriedById.lastName',
      'retriedByUser.second_name',
      'retriedByUser.last_name',
      'retriedByUser.lastName',
    ]);
    const fullName = `${firstName || ''} ${secondName || ''}`.trim();
    return fullName || '-';
  };

  const columns: object[] = [
    {
      title: 'No',
      dataIndex: 'no',
      key: 'no',
      width: 80,
    },
    {
      title: 'Driver',
      dataIndex: 'driver',
      key: 'driver',
      width: 220,
    },
    {
      title: 'Company',
      dataIndex: 'company',
      key: 'company',
      width: 220,
    },
    {
      title: 'Start date',
      dataIndex: 'startDate',
      key: 'startDate',
      width: 140,
    },
    {
      title: 'End date',
      dataIndex: 'endDate',
      key: 'endDate',
      width: 140,
    },
    {
      title: 'Created at',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (value: string) => {
        const normalized = (value || '').toLowerCase();
        if (normalized.includes('fail') || normalized.includes('error')) {
          return <Tag color="red">{value || 'FAILED'}</Tag>;
        }
        return <Tag color="orange">{value || 'FAILED'}</Tag>;
      },
    },
    {
      title: 'Error',
      dataIndex: 'error',
      key: 'error',
      render: (value: string) => value || '-',
    },
    {
      title: 'Actions',
      dataIndex: 'action',
      key: 'action',
      width: 220,
      fixed: 'right',
      render: (_: any, row: FailedItem) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button onClick={() => handleShowDetails(row)}>Details</Button>
          <Button
            type="primary"
            onClick={() => handleRetry(row.id)}
            loading={retryingId === row.id}
            disabled={!row.id}
          >
            Retry
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
        <Button style={{ marginBottom: 6 }} size="large" onClick={refetch}>
          Refresh
        </Button>
      </div>

      <Spin size="large" spinning={isLoading || isFetching}>
        <Table
          style={{ margin: '15px 0 0 0' }}
          onChange={onChange}
          dataSource={data?.map((row: any, index: number): FailedItem => {
            const id = getFirstValue(row, ['id', '_id']);
            const firstName = getFirstValue(row, ['driver.first_name', 'driver.firstName']);
            const lastName = getFirstValue(row, ['driver.second_name', 'driver.lastName']);
            const companyName = getFirstValue(row, ['company.name', 'companyName']);
            const startRaw = getFirstValue(row, ['start_date', 'startDate']);
            const endRaw = getFirstValue(row, ['end_date', 'endDate']);
            const createdRaw = getFirstValue(row, ['createdAt', 'created_at']);
            const statusRaw = getFirstValue(row, ['status', 'report_status']);
            const errorRaw = getFirstValue(row, ['error', 'message', 'reason']);

            return {
              no: calcOrder(skip, 10, index),
              key: id ?? `${skip}-${index}`,
              id: id ?? '',
              driver: `${firstName || ''} ${lastName || ''}`.trim() || '-',
              company: companyName ? String(companyName) : '-',
              startDate: startRaw ? moment(startRaw).format('MM-DD-YYYY') : '-',
              endDate: endRaw ? moment(endRaw).format('MM-DD-YYYY') : '-',
              createdAt: createdRaw ? moment(createdRaw).format('MM-DD-YYYY') : '-',
              status: statusRaw ? String(statusRaw) : 'failed',
              error: errorRaw ? String(errorRaw) : '-',
              raw: row,
            };
          })}
          columns={columns}
          pagination={{ total: total, showSizeChanger: false, current: skip }}
          scroll={{ x: 'max-content' }}
        />
      </Spin>

      <Modal
        title="Failed FMCSA Details"
        open={detailsOpen}
        onCancel={() => setDetailsOpen(false)}
        footer={null}
        width={900}
      >
        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item label="type">
            {String(getFirstValue(selectedDetails, ['type']) ?? '-')}
          </Descriptions.Item>
          <Descriptions.Item label="submissionId">
            {String(getFirstValue(selectedDetails, ['submissionId']) ?? '-')}
          </Descriptions.Item>
          <Descriptions.Item label="fileCode">
            {String(getFirstValue(selectedDetails, ['fileCode', 'filecode']) ?? '-')}
          </Descriptions.Item>
          <Descriptions.Item label="failureReason">
            {String(getFirstValue(selectedDetails, ['failureReason']) ?? '-')}
          </Descriptions.Item>
          <Descriptions.Item label="failureStage">
            {String(getFirstValue(selectedDetails, ['failureStage']) ?? '-')}
          </Descriptions.Item>
          <Descriptions.Item label="lastFailureAt">
            {formatDateTime(getFirstValue(selectedDetails, ['lastFailureAt']))}
          </Descriptions.Item>
          <Descriptions.Item label="lastRetryAt">
            {formatDateTime(getFirstValue(selectedDetails, ['lastRetryAt']))}
          </Descriptions.Item>
          <Descriptions.Item label="retryCount">
            {String(getFirstValue(selectedDetails, ['retryCount']) ?? '-')}
          </Descriptions.Item>
          <Descriptions.Item label="processedTime">
            {formatDateTime(getFirstValue(selectedDetails, ['processedTime']))}
          </Descriptions.Item>
          <Descriptions.Item label="retriedById">
            {renderRetriedBy(selectedDetails)}
          </Descriptions.Item>
          <Descriptions.Item label="failureDetails (full object)">
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {stringifyDetails(getFirstValue(selectedDetails, ['failureDetails']))}
            </pre>
          </Descriptions.Item>
        </Descriptions>
      </Modal>
    </div>
  );
};

export default FmcsaFailedFromApp;
